// =============================================================================
// DOCUMENT TYPE SYSTEM
// =============================================================================
// This system is designed to be flexible and backward-compatible:
// 1. Document types are stored as simple strings in the database
// 2. Labels are looked up dynamically - if a type is renamed, old data still works
// 3. All types use singular snake_case (e.g., "insurance_document" not "insurance_documents")
// 4. Legacy values are mapped to current types via LEGACY_TYPE_MAP
// =============================================================================

// Canonical document types - singular, snake_case
// These are the CURRENT types used for new uploads
export const DocumentType = {
  DEATH_CERTIFICATE: "death_certificate",
  WILL: "will",
  TRUST: "trust",
  IDENTIFICATION_CARD: "identification_card",
  DECEDENT_ID: "decedent_id",
  INSURANCE_DOCUMENT: "insurance_document",
  TAX_DOCUMENT: "tax_document",
  BANK_STATEMENT: "bank_statement",
  INVESTMENT_ACCOUNT_STATEMENT: "investment_account_statement",
  PROPERTY_DEED: "property_deed",
  VEHICLE_TITLE: "vehicle_title",
  CREDIT_REPORT: "credit_report",
  MORTGAGE_STATEMENT: "mortgage_statement",
  CREDIT_CARD_STATEMENT: "credit_card_statement",
  ANNUITY_CONTRACT: "annuity_contract",
  UTILITY_BILL: "utility_bill",
  GENERATED_LETTER: "generated_letter",
  DISCHARGE_PAPERS: "discharge_papers",
  FUNERAL_INVOICE: "funeral_invoice",
  THIRD_PARTY_AUTHORIZATION: "third_party_authorization",
  PAYMENT_RECEIPT: "payment_receipt",
  LETTERS_TESTAMENTARY: "letters_testamentary",
  LETTERS_ADMINISTRATION: "letters_administration",
  CERTIFICATION_OF_TRUST: "certification_of_trust",
  TRUST_AMENDMENT: "trust_amendment",
  NOTICE_TO_BENEFICIARIES: "notice_to_beneficiaries",
  EIN_CONFIRMATION: "ein_confirmation",
  OTHER: "other",
} as const;

export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];

export interface DocumentMetadata {
  id: number;
  document_key: string;
  user_email: string;
  uploaded_by_email: string;
  document_type: DocumentType;
  type_label?: string;
  file_name: string;
  display_name?: string | null;
  file_size: number;
  created_at: string;
  updated_at: string;
  account_id?: number;
  asset_id?: number;
  task_id?: number;
  extraction_status:
    | "no_extraction"
    | "extraction_in_progress"
    | "extraction_complete"
    | "extraction_failed";
  physical_location?: string;
  is_confirmation?: boolean;
  duplicate_of_name?: string | null;
  upload_session_id?: number | null;
  recipient_name?: string | null;
  folder_id?: number | null;
}

// Document extraction status enum (must match database check constraint)
export const DocumentExtractionStatus = {
  NoExtraction: "pending",
  ExtractionInProgress: "in_progress",
  ExtractionComplete: "completed",
  ExtractionFailed: "failed",
  ExtractionPartial: "partial",
} as const;

export type DocumentExtractionStatus =
  (typeof DocumentExtractionStatus)[keyof typeof DocumentExtractionStatus];

// Database row type for estate_documents table (for direct Supabase queries)
export interface PersonDocumentRow {
  id: number;
  document_key: string;
  estate_id: number;
  uploaded_by_user_id: number;
  document_type: string;
  file_name: string | null;
  created_at: string;
  uploaded_from_task_id: number | null;
  extraction_status: "pending" | "in_progress" | "completed" | "failed" | "partial";
  smart_summary: Record<string, unknown> | null;
  document_extraction: Record<string, unknown> | null;
  extraction_started_at: string | null;
  extraction_completed_at: string | null;
  extraction_error: string | null;
  specific_doc_extraction: Record<string, unknown> | null;
  specific_doc_citations: Record<string, unknown> | null;
  is_confirmation: boolean | null;
  display_name: string | null;
}

// User document interface (for compatibility with existing code)
export interface UserDocument {
  id: number;
  name?: string;
  documentType: DocumentType;
  extractionStatus: DocumentExtractionStatus;
  fileName: string;
  fileSize: number;
  createdAt: string;
  updatedAt: string;
  size?: number;
}

// Document detail for staged/selected documents
// Uses string for documentType to support legacy/dynamic types
export interface DocumentDetail {
  id: number;
  name: string;
  fileName: string;
  documentType: string;
  createdAt: Date;
}

// Document extension type
export type DocumentExtension = "application/pdf" | "image/jpeg" | "image/png" | "other";

// Smart summary type
export interface SmartSummary {
  status: "pending" | "processing" | "completed" | "failed";
  summary?: string;
  error?: string;
  feedback?: "not_answered" | "good" | "bad";
}

// Extracted contact from document parsing
export interface ExtractedContact {
  full_name?: string;
  first_name?: string;
  last_name?: string;
  relationship?: string;
  legal_role?: string[];
  contact_info?: {
    email?: string;
    phone?: string;
    address?: string;
  };
  // Legacy fields for backward compatibility
  email?: string;
  phone?: string;
  address?: string;
}

// Extracted financial item from document parsing
export interface ExtractedFinancialItem {
  name?: string;
  item_name?: string;
  category?: string;
  balance?: number;
  value?: number;
  account_number?: string;
  institution?: string;
  notes?: string;
}

// Extracted beneficiary from document parsing
export interface ExtractedBeneficiary {
  name: string;
  relationship?: string;
  share?: string | number;
  is_charity?: boolean;
}

// Document extraction data (generic structure for all document types)
export interface DocumentExtraction {
  contacts?: { contacts: ExtractedContact[] };
  assets?: ExtractedFinancialItem[];
  debts?: ExtractedFinancialItem[];
  beneficiaries?: { beneficiaries: ExtractedBeneficiary[] };
  action_fields?: Record<string, string | number | boolean | null>;
  // Document-type specific extractions (structure varies by document type)
  will_data?: Record<string, unknown>;
  trust_data?: Record<string, unknown>;
  trust_amendment_data?: Record<string, unknown>;
  death_certificate_data?: Record<string, unknown>;
  // Metadata
  extracted_at?: string;
  extraction_version?: string;
}

// User document with ID (for compatibility)
export interface UserDocumentWithId extends UserDocument {
  extension: DocumentExtension;
  previewUrl?: string;
  // API response properties (snake_case)
  file_name?: string;
  display_name?: string | null;
  document_type?: DocumentType;
  created_at?: string;
  smart_summary?: SmartSummary | null;
  document_extraction?: DocumentExtraction | null;
  // Document-type-specific extraction (Will, Trust, etc.)
  specific_doc_extraction?: Record<string, unknown> | null;
  specific_doc_citations?: Record<string, unknown> | null;
}

// Document selection interface
export interface DocumentSelection {
  id: string;
  file: File;
  documentType: DocumentType;
  extension: DocumentExtension;
  previewUrl: string;
  createdAt: Date;
  physicalLocation: string;
}

// Document types array for compatibility
export const documentTypes = Object.entries(DocumentType).map(([, value]) => ({
  id: value,
  label: value,
}));

// Allowed document types for filtering and upload dropdown (only types users can select)
// Keys are snake_case DB values, values are display labels
// Order matters - this is the order they appear in dropdowns
export const allowedDocumentTypes = new Map([
  [DocumentType.DEATH_CERTIFICATE, "Death Certificate"],
  [DocumentType.WILL, "Will"],
  [DocumentType.TRUST, "Trust"],
  [DocumentType.IDENTIFICATION_CARD, "Identification Card"],
  [DocumentType.DECEDENT_ID, "Decedent's Identification Card"],
  [DocumentType.INSURANCE_DOCUMENT, "Insurance Document"],
  [DocumentType.TAX_DOCUMENT, "Tax Document"],
  [DocumentType.BANK_STATEMENT, "Bank Statement"],
  [DocumentType.INVESTMENT_ACCOUNT_STATEMENT, "Investment Account Statement"],
  [DocumentType.PROPERTY_DEED, "Property Deed"],
  [DocumentType.VEHICLE_TITLE, "Vehicle Title"],
  [DocumentType.CREDIT_REPORT, "Credit Report"],
  [DocumentType.MORTGAGE_STATEMENT, "Mortgage Statement"],
  [DocumentType.CREDIT_CARD_STATEMENT, "Credit Card Statement"],
  [DocumentType.ANNUITY_CONTRACT, "Annuity Contract"],
  [DocumentType.UTILITY_BILL, "Utility Bill"],
  [DocumentType.DISCHARGE_PAPERS, "Discharge Papers (DD-214)"],
  [DocumentType.FUNERAL_INVOICE, "Funeral Invoice"],
  [DocumentType.LETTERS_TESTAMENTARY, "Letters Testamentary"],
  [DocumentType.LETTERS_ADMINISTRATION, "Letters of Administration"],
  [DocumentType.CERTIFICATION_OF_TRUST, "Certification of Trust"],
  [DocumentType.TRUST_AMENDMENT, "Trust Amendment"],
  [DocumentType.OTHER, "Other"],
]);

// Ready-to-use array for dropdowns/selects - USE THIS instead of defining your own arrays
export const DOCUMENT_TYPE_OPTIONS = Array.from(allowedDocumentTypes.entries()).map(
  ([id, label]) => ({ id, label }),
);
